import React from "react";
import { Link } from "react-router-dom";
import { Lock, Clock } from "lucide-react";
import SectionSponsor from "./SectionSponsor";

const FALLBACK_IMG =
  "https://images.unsplash.com/photo-1595432849291-3036ea5e5475?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const onImgError = (e) => {
  if (e.currentTarget.src !== FALLBACK_IMG) e.currentTarget.src = FALLBACK_IMG;
};

function fmtDate(iso) {
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export function SubscriberBadge({ className = "", compact = false }) {
  return (
    <span
      className={`eyebrow inline-flex items-center gap-1 whitespace-nowrap shrink-0 rounded-sm bg-[#1E3A8A] px-1.5 py-0.5 text-[9px] text-[#FEF08A] ${className}`}
    >
      <Lock size={9} /> {compact ? "Exclusive" : "Subscriber Exclusive"}
    </span>
  );
}

function CategoryTag({ category }) {
  const labels = {
    news: "Local News",
    sports: "High School Sports",
    entertainment: "Entertainment",
    columnists: "Columnists",
  };
  return <span className="eyebrow whitespace-nowrap text-[var(--gold)]">{labels[category] || category}</span>;
}

// Hero / lead feature card
export function FeatureArticle({ article }) {
  return (
    <article className="fade-up" data-testid={`feature-${article.slug}`}>
      <Link to={`/article/${article.slug}`} className="group block">
        <div className="img-zoom mb-4 overflow-hidden rounded-lg">
        <img src={article.image || FALLBACK_IMG} alt={article.title} onError={onImgError} className="h-72 w-full object-cover sm:h-[26rem]" />
        </div>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <CategoryTag category={article.category} />
          {article.breaking && (
            <span className="eyebrow rounded-sm bg-[var(--breaking)] px-1.5 py-0.5 text-[9px] text-white">Breaking</span>
          )}
        </div>
        <h2 className="font-display mt-2 text-3xl font-extrabold leading-tight tracking-tight text-[var(--ink)] group-hover:text-[var(--navy)] sm:text-4xl">
          {article.title}
        </h2>
        <p className="font-serif-body mt-3 text-base leading-relaxed text-slate-700 sm:text-lg">{article.excerpt}</p>
        <div className="font-ui mt-3 flex items-center gap-3 text-xs text-slate-500">
          <span>By {article.author_name}</span>
          <span>·</span>
          <span>{fmtDate(article.published_at)}</span>
          <span className="flex items-center gap-1"><Clock size={12} /> {article.read_time} min</span>
        </div>
      </Link>
    </article>
  );
}

// Standard grid card
export function ArticleCard({ article, horizontal = false }) {
  if (horizontal) {
    return (
      <article className="card-hover flex gap-4 border-b border-[var(--paper-border)] pb-5" data-testid={`article-${article.slug}`}>
        <Link to={`/article/${article.slug}`} className="img-zoom hidden h-24 w-32 shrink-0 overflow-hidden rounded sm:block">
          <img src={article.image || FALLBACK_IMG} alt={article.title} onError={onImgError} className="h-full w-full object-cover" />
        </Link>
        <div>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <CategoryTag category={article.category} />
          </div>
          <Link to={`/article/${article.slug}`}>
            <h3 className="font-display mt-1 text-lg font-bold leading-snug text-[var(--ink)] hover:text-[var(--navy)]">
              {article.title}
            </h3>
          </Link>
          <p className="font-serif-body mt-1 line-clamp-2 text-sm text-slate-600">{article.excerpt}</p>
          <p className="font-ui mt-1 text-xs text-slate-500">{article.author_name} · {fmtDate(article.published_at)}</p>
        </div>
      </article>
    );
  }
  return (
    <article className="card-hover group flex flex-col" data-testid={`article-${article.slug}`}>
      <Link to={`/article/${article.slug}`} className="img-zoom mb-3 overflow-hidden rounded-lg">
        <img src={article.image || FALLBACK_IMG} alt={article.title} onError={onImgError} className="h-48 w-full object-cover" />
      </Link>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <CategoryTag category={article.category} />
      </div>
      <Link to={`/article/${article.slug}`}>
        <h3 className="font-display mt-1.5 text-xl font-bold leading-snug text-[var(--ink)] group-hover:text-[var(--navy)]">
          {article.title}
        </h3>
      </Link>
      <p className="font-serif-body mt-2 line-clamp-3 flex-1 text-sm leading-relaxed text-slate-600">{article.excerpt}</p>
      <div className="font-ui mt-3 flex items-center gap-2 text-xs text-slate-500">
        {article.author_avatar && <img src={article.author_avatar} alt={article.author_name} onError={(e) => { e.currentTarget.style.display = "none"; }} className="h-6 w-6 rounded-full object-cover" />}
        <span>{article.author_name}</span>
        <span>·</span>
        <span>{fmtDate(article.published_at)}</span>
      </div>
    </article>
  );
}

export function SectionHeading({ title, tagline, to, sectionKey }) {
  return (
    <div className="section-rule mb-6 flex items-end justify-between gap-4 pt-3">
      <div>
        <h2 className="font-display text-2xl font-bold tracking-tight text-[var(--navy)] sm:text-3xl">{title}</h2>
        {tagline && <p className="font-ui mt-0.5 text-xs uppercase tracking-wider text-slate-500">{tagline}</p>}
      </div>
      <div className="flex shrink-0 items-center gap-3">
        {sectionKey && <SectionSponsor section={sectionKey} />}
        {to && (
          <Link to={to} className="font-ui text-xs font-semibold uppercase tracking-wider text-[var(--gold)] hover:underline">
            More →
          </Link>
        )}
      </div>
    </div>
  );
}

export { fmtDate };
